import * as React from 'react';
import styled, { css } from 'styled-components';
import AspectRatio from '../Layout/AspectRatio';
import { IDSContentfulVideoFile } from '@ids/types/contentful';
import usePrefersReducedMotion from '@ids/hooks/usePrefersReducedMotion';
import useInView from '@ids/hooks/useInView';
import { useAppshellMeta } from '@ids/appshell';

const StyledVideo = styled.video<{ $backgroundImage?: string; $videoLoaded: boolean }>`
    width: 100%;
    height: 100%;
    object-fit: cover;

    ${({ $backgroundImage, $videoLoaded }) =>
        $backgroundImage &&
        !$videoLoaded &&
        css`
            background: url(${$backgroundImage}) center center / cover no-repeat;
        `}
`;

/**
 * This hook is used to avoid an issue on iOS devices when the video is set to
 * autoplay and it has a poster image.
 *
 * The issue is that there is a flash before the video starts playing and
 * right after the poster has been removed.
 */
export const useVideoPoster = (autoPlay: boolean, posterUrl?: string) => {
    if (!posterUrl) {
        return undefined;
    }

    if (autoPlay) {
        return {
            $backgroundImage: posterUrl,
        };
    }

    return {
        poster: posterUrl,
    };
};

interface MediaVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
    /**
     * Accepts a boolean value.
     * If true it will preload the video component.
     *
     * @default false
     */
    priority?: boolean;
    /* eslint-disable-next-line react/no-unused-prop-types */
    ratio?: number;
    video: IDSContentfulVideoFile;
}

const loadLazyVideo = (video: HTMLVideoElement) => {
    video.load();

    const [videoSource] = Array.from(video.children) as HTMLSourceElement[];

    videoSource.src = videoSource.dataset.src ?? '';
};

const MediaVideo = ({
    video: { autoPlay = true, loop = true, muted = true, videoFile, poster },
    priority,
    ...videoProps
}: MediaVideoProps) => {
    const { isApp, getDeviceTypeByUserAgent } = useAppshellMeta();
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [videoLoaded, setVideoLoaded] = React.useState(false);
    const prefersReducedMotion = usePrefersReducedMotion();
    const shouldVideoAutoPlay = React.useMemo(
        () => !!((autoPlay || videoProps.autoPlay) && !prefersReducedMotion),
        [autoPlay, prefersReducedMotion, videoProps.autoPlay]
    );
    const videoPoster = useVideoPoster(shouldVideoAutoPlay, poster?.file.url ?? videoProps.poster);
    const isVideoInView = useInView({
        containerRef: videoRef,
        options: { rootMargin: '100px' },
    });

    React.useEffect(() => {
        const videoElement = videoRef.current;

        if (videoElement && isVideoInView) {
            if (!priority) {
                loadLazyVideo(videoElement);
            }

            videoElement[shouldVideoAutoPlay ? 'play' : 'pause']();
            videoElement.controls = !shouldVideoAutoPlay;

            /*
             * Temporary solution until iOS team releases a fix for allowing
             * inline media playback on our webviews regardless of where the
             * user is navigated from.
             */
            if (videoElement.paused && isApp && getDeviceTypeByUserAgent() === 'IOS') {
                videoElement.pause();
                videoElement.controls = true;
            }
        }
    }, [getDeviceTypeByUserAgent, isApp, isVideoInView, priority, shouldVideoAutoPlay]);

    return (
        <StyledVideo
            ref={videoRef}
            loop={loop ?? videoProps.loop}
            preload={priority ? 'auto' : 'metadata'}
            muted={muted ?? videoProps.muted}
            title={videoFile.title ?? videoProps.title}
            playsInline
            onPlaying={() => setTimeout(() => setVideoLoaded(true), 1000)}
            $videoLoaded={videoLoaded}
            {...videoPoster}
            {...videoProps}
        >
            <source
                key={videoFile.title}
                {...{ [priority ? 'src' : 'data-src']: videoFile.file.url }}
                type={videoFile.file.contentType}
            />
        </StyledVideo>
    );
};

interface MediaVideoWithLayoutProps extends MediaVideoProps {
    ratio?: number;
}

const MediaVideoWithLayout = ({ video, priority, ratio, ...videoProps }: MediaVideoWithLayoutProps) => {
    if (!video.videoFile) {
        return null;
    }

    return ratio ? (
        <AspectRatio ratio={ratio}>
            <MediaVideo priority={priority} video={video} {...videoProps} />
        </AspectRatio>
    ) : (
        <MediaVideo priority={priority} video={video} {...videoProps} />
    );
};

MediaVideo.Full = MediaVideoWithLayout;

export { MediaVideoWithLayout as default, MediaVideo };
